import { useMemo } from 'react';
import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import Typography from '@mui/material/Typography';
import { getAuthenticatedSession } from 'src/api/authentication';
import { AuthenticatedUserProvider } from 'src/contexts/AuthenticatedUserContext';
import { BreadcrumbProvider } from 'src/contexts/BreadcrumbContext';
import { ConfigProvider } from 'src/contexts/ConfigContext';
import SupervisorLayoutWrapper from 'src/layouts/supervisor';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { lightTheme } from '@repo/theme';
import { AuthenticatedUser, PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getCustomerThemeSettings } from 'src/api/admin/settings';
import { getConfig } from 'src/api/config';
import { Tabs } from '@repo/components';

export const Route = createFileRoute('/supervisor_')({
	staleTime: 1000 * 60 * 5,
	loader: async () => {
		const authenticatedSession = await getAuthenticatedSession();
		if (!authenticatedSession.user) {
			throw redirect({
				to: '/authentication/login',
				search: {
					redirect: location.href,
				},
			});
		}

		const config = await getConfig();
		if (!config?.featureFlags?.USES_SUPERVISOR) {
			throw redirect({
				to: '/403',
				search: {
					message: 'Supervisor portal is not enabled',
				},
			});
		}

		const verifyPermission = getVerifyPermission(authenticatedSession.user.permissions || []);
		if (!verifyPermission([PERMISSIONS.supervisorView])) {
			throw redirect({
				to: '/403',
				search: {
					permissions: 'supervisorView',
					occurredAt: '/supervisor',
					method: 'GET',
				},
			});
		}

		const themeSettings = await getCustomerThemeSettings();

		return { authenticatedSession, themeSettings, config };
	},
	component: SupervisorLayout,
});

const tabs = [
	{ id: 'users', label: 'Users' },
	{ id: 'logs', label: 'Logs' },
];

const tabIndexPerTabId: Record<string, number> = tabs.reduce(
	(acc, cur, index) => ({ ...acc, [cur.id]: index }),
	{} as Record<string, number>,
);

function SupervisorLayout() {
	const { authenticatedSession, themeSettings, config } = Route.useLoaderData();
	const navigate = useNavigate();
	const location = useLocation();

	const activeTabId = useMemo(() => {
		const path = location.pathname;
		if (path.includes('/supervisor/logs')) return 'logs';
		return 'users';
	}, [location.pathname]);

	const handleTabChange = (tabIndex: number) => {
		const targetTab = tabs[tabIndex];
		if (targetTab) {
			navigate({ to: `/supervisor/${targetTab.id}` });
		}
	};

	return (
		<ThemeProvider theme={lightTheme}>
			<CssBaseline />
			<AuthenticatedUserProvider authenticatedSession={authenticatedSession}>
				<ConfigProvider>
					<BreadcrumbProvider>
						<SupervisorLayoutWrapper
							authenticatedUser={(authenticatedSession?.user as AuthenticatedUser) || null}
							themeSettings={themeSettings}
							featureFlags={config?.featureFlags}
						>
							<div className="px-4">
								<div className="relative pt-3">
									<div className="flex justify-between gap-2">
										<div className="w-full">
											<Typography variant="h4" className="mb-1">
												Supervisor Portal
											</Typography>
										</div>
										{themeSettings?.settings?.logo?.link && (
											<div className="flex flex-col justify-start items-center">
												<img src={themeSettings?.settings?.logo?.link} className="h-10" alt="Logo" />
											</div>
										)}
									</div>
								</div>
								{/* <Tabs value={tabIndexPerTabId[activeTabId] ?? 0} onChange={handleTabChange}>
									<Tabs.TabList>
										{tabs.map((tab) => (
											<Tabs.Tab key={tab.id} value={tabIndexPerTabId[tab.id] ?? 0} label={tab.label} />
										))}
									</Tabs.TabList>
								</Tabs> */}
								<Outlet />
							</div>
						</SupervisorLayoutWrapper>
					</BreadcrumbProvider>
				</ConfigProvider>
			</AuthenticatedUserProvider>
		</ThemeProvider>
	);
}
